// File: /src/hooks/useFinanceData.js

import { useState, useCallback, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { AuthError } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';

const createEmptySummary = () => ({
	totalReceived: 0,
	totalPending: 0,
	totalOverdue: 0,
	totalIncome: 0,
	totalExpense: 0,
	netTotal: 0,
});

const normalizeSummary = (summary = {}) => {
	return {
		totalReceived: Number(summary.totalReceived) || 0,
		totalPending: Number(summary.totalPending) || 0,
		totalOverdue: Number(summary.totalOverdue) || 0,
		totalIncome: Number(summary.totalIncome) || 0,
		totalExpense: Number(summary.totalExpense) || 0,
		// netTotal now comes from backend (single source of truth)
		netTotal: Number(summary.netTotal) || 0,
	};
};

let financeCache = {
	financeSummary: null,
	manualEntries: null,
	paidInvoices: null,
	allInvoices: null,
	projects: null,
	clients: null,
};

let financeInitialized = false;
let inFlightPromise = null;
let financeCacheToken = null;

export const useFinanceData = () => {
	const { token } = useAuth();
	const [financeSummary, setFinanceSummaryState] = useState(
		financeCache.financeSummary ?? createEmptySummary(),
	);
	const [manualEntries, setManualEntriesState] = useState(
		financeCache.manualEntries ?? [],
	);
	const [paidInvoices, setPaidInvoicesState] = useState(
		financeCache.paidInvoices ?? [],
	);
	const [allInvoices, setAllInvoicesState] = useState(
		financeCache.allInvoices ?? [],
	);
	const [projects, setProjectsState] = useState(financeCache.projects ?? []);
	const [clients, setClientsState] = useState(financeCache.clients ?? []);
	const [loading, setLoading] = useState(!financeInitialized);
	const [error, setError] = useState(null);

	const updateCacheAndState = useCallback((setter, cacheKey) => {
		return (updater) => {
			setter((prev) => {
				const next =
					typeof updater === 'function' ? updater(prev) : updater;
				financeCache[cacheKey] = next;
				return next;
			});
		};
	}, []);

	const setFinanceSummary = updateCacheAndState(
		setFinanceSummaryState,
		'financeSummary',
	);
	const setManualEntries = updateCacheAndState(
		setManualEntriesState,
		'manualEntries',
	);
	const setPaidInvoices = updateCacheAndState(
		setPaidInvoicesState,
		'paidInvoices',
	);
	const setAllInvoices = updateCacheAndState(
		setAllInvoicesState,
		'allInvoices',
	);
	const setProjects = updateCacheAndState(setProjectsState, 'projects');
	const setClients = updateCacheAndState(setClientsState, 'clients');

	const fetchFinanceData = useCallback(async (force = false, tokenSnapshot = financeCacheToken) => {
		if (!tokenSnapshot) {
			return;
		}

		if (!force && financeInitialized && financeCache.financeSummary) {
			setFinanceSummaryState(financeCache.financeSummary);
			setManualEntriesState(financeCache.manualEntries ?? []);
			setPaidInvoicesState(financeCache.paidInvoices ?? []);
			setAllInvoicesState(financeCache.allInvoices ?? []);
			setProjectsState(financeCache.projects ?? []);
			setClientsState(financeCache.clients ?? []);
			setLoading(false);
			return;
		}

		if (inFlightPromise && !force) {
			await inFlightPromise;
			return;
		}

		setLoading(true);

		const loadData = async () => {
			try {
				const [
					projectsRes,
					clientsRes,
					summaryRes,
					entriesRes,
					invoicesRes,
				] = await Promise.all([
					apiClient.get(API_ENDPOINTS.PROJECTS),
					apiClient.get(API_ENDPOINTS.CLIENTS),
					apiClient.get(API_ENDPOINTS.FINANCE_SUMMARY),
					apiClient.get(API_ENDPOINTS.FINANCE_ENTRIES),
					apiClient.get(API_ENDPOINTS.INVOICES),
				]);

				const normalizedSummary = normalizeSummary(
					summaryRes.data || {},
				);
				const manualEntriesData = entriesRes.data || [];
				const invoicesData = invoicesRes.data || [];
				const paidInvoicesData = invoicesData.filter(
					(inv) => inv.status === 'paid',
				);

				if (financeCacheToken !== tokenSnapshot) {
					return;
				}

				financeCache = {
					financeSummary: normalizedSummary,
					manualEntries: manualEntriesData,
					paidInvoices: paidInvoicesData,
					allInvoices: invoicesData,
					projects: projectsRes.data || [],
					clients: clientsRes.data || [],
				};
				financeInitialized = true;

				setFinanceSummaryState(normalizedSummary);
				setManualEntriesState(manualEntriesData);
				setPaidInvoicesState(paidInvoicesData);
				setAllInvoicesState(invoicesData);
				setProjectsState(projectsRes.data || []);
				setClientsState(clientsRes.data || []);
				setError(null);
			} catch (err) {
				if (err instanceof AuthError) {
					return;
				}
				console.error('Erro ao carregar dados financeiros:', err);
				setError(err);
			} finally {
				if (financeCacheToken === tokenSnapshot) {
					setLoading(false);
				}
			}
		};

		inFlightPromise = loadData();
		await inFlightPromise;
		inFlightPromise = null;
	}, []);

	useEffect(() => {
		if (!token) {
			financeCache = {
				financeSummary: null,
				manualEntries: null,
				paidInvoices: null,
				allInvoices: null,
				projects: null,
				clients: null,
			};
			financeInitialized = false;
			financeCacheToken = null;
			inFlightPromise = null;
			setFinanceSummaryState(createEmptySummary());
			setManualEntriesState([]);
			setPaidInvoicesState([]);
			setAllInvoicesState([]);
			setProjectsState([]);
			setClientsState([]);
			setLoading(false);
			return;
		}

		if (financeCacheToken && financeCacheToken !== token) {
			financeCache = {
				financeSummary: null,
				manualEntries: null,
				paidInvoices: null,
				allInvoices: null,
				projects: null,
				clients: null,
			};
			financeInitialized = false;
			inFlightPromise = null;
			setFinanceSummaryState(createEmptySummary());
			setManualEntriesState([]);
			setPaidInvoicesState([]);
			setAllInvoicesState([]);
			setProjectsState([]);
			setClientsState([]);
			setLoading(true);
			fetchFinanceData(true, token).catch(() => {
				setLoading(false);
			});
		} else if (!financeInitialized) {
			setLoading(true);
			fetchFinanceData(true, token).catch(() => {
				setLoading(false);
			});
		}

		financeCacheToken = token;
	}, [token, fetchFinanceData]);

	return {
		financeSummary,
		setFinanceSummary,
		manualEntries,
		setManualEntries,
		paidInvoices,
		setPaidInvoices,
		allInvoices,
		setAllInvoices,
		projects,
		setProjects,
		clients,
		setClients,
		loading,
		error,
		refreshFinanceData: (force = false) => fetchFinanceData(force, financeCacheToken),
	};
};

export default useFinanceData;
